import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, CalendarIcon, CheckCircleIcon, MapPinIcon, InfoIcon } from 'lucide-react';
import { VaccineAppointmentModal } from '../components/VaccineAppointmentModal';
export function Immunizations() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState('');
  const openModal = (vaccine: string) => {
    setSelectedVaccine(vaccine);
    setModalOpen(true);
  };
  const vaccineCatalog = [{
    name: 'COVID-19 Booster',
    desc: 'Updated seasonal booster for protection against current variants.',
    age: '6 months+',
    price: 'Covered by most insurance'
  }, {
    name: 'Flu (Seasonal)',
    desc: 'Annual quadrivalent vaccine to protect against seasonal influenza.',
    age: '3 years+',
    price: '$25 or covered by insurance'
  }, {
    name: 'Tdap',
    desc: 'Protection against Tetanus, Diphtheria, and Pertussis (Whooping Cough).',
    age: '11 years+',
    price: 'Covered by most insurance'
  }, {
    name: 'Pneumonia',
    desc: 'Prevents pneumococcal disease and related complications.',
    age: '65+ or high risk',
    price: 'Covered by most insurance'
  }, {
    name: 'Shingles',
    desc: 'Two-dose series to prevent shingles and post-herpetic neuralgia.',
    age: '50 years+',
    price: 'Covered by most insurance'
  }, {
    name: 'Travel Vaccinations',
    desc: 'Hepatitis A/B, Typhoid, and other region-specific protections.',
    age: 'Varies',
    price: 'Varies by vaccine'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="bg-white min-h-screen pb-20" data-id="element-428" data-mp="2di910b5hzlqd">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 mb-12 md:mb-16" data-id="element-429" data-mp="361obukreu49t">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-clinical-blue/5 text-clinical-blue rounded-full text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-430" data-mp="319o9klnvaza2">
          Clinical Services
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 uppercase tracking-tighter" data-id="element-431" data-mp="14a5raw0wbpmh">
          Immunization Services
        </h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed" data-id="element-432" data-mp="ulnv6s3ka6p6">
          Protect yourself and your loved ones. Our certified pharmacists
          provide a wide range of vaccinations in a safe, clinical environment.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-433" data-mp="kp1ac3b8jfn5">
        <div className="grid lg:grid-cols-3 gap-12 items-start" data-id="element-434" data-mp="6oi513y5mnoo">
          <div className="lg:col-span-2 space-y-8" data-id="element-435" data-mp="6q1jcp092ont">
            <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm" data-id="element-436" data-mp="bnkyrs8op7hc">
              <div className="p-6 sm:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center" data-id="element-437" data-mp="21316d36yml54">
                <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3" data-id="element-438" data-mp="2601d7gbmvrxa">
                  <ShieldCheckIcon className="w-6 h-6 text-clinical-blue" data-id="element-439" data-mp="3qgio5p6vapov" />
                  Available Vaccines
                </h2>
                <div className="hidden sm:flex items-center gap-2 text-clinical-blue font-bold text-[10px] uppercase tracking-widest" data-id="element-440" data-mp="43sl3l3gaiwq">
                  <span className="w-2 h-2 bg-clinical-blue rounded-full animate-pulse" data-id="element-441" data-mp="3vgdgzya7dpck"></span>
                  In Stock
                </div>
              </div>
              <div className="divide-y divide-slate-50" data-id="element-442" data-mp="85dsiqx7u4zj">
                {vaccineCatalog.map((v, i) => <div key={i} className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:bg-slate-50/50 transition-colors" data-id="element-443" data-mp="n2elsyv22804" data-mpi={i}>
                    <div className="max-w-md" data-id="element-444" data-mp="2ouawy7db9fud">
                      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-445" data-mp="2juers4svnid8">
                        {v.name}
                      </h3>
                      <p className="text-slate-500 text-sm mb-3 leading-relaxed" data-id="element-446" data-mp="1awexee3p7m3q">
                        {v.desc}
                      </p>
                      <div className="flex flex-wrap gap-4" data-id="element-447" data-mp="1fynrjdsb4u7f">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-448" data-mp="1kwpck49an3lj">
                          <InfoIcon className="w-3 h-3" data-id="element-449" data-mp="1ov566qzvra7x" /> Age: {v.age}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-clinical-blue uppercase tracking-widest" data-id="element-450" data-mp="s4vv69hi0c2r">
                          <CheckCircleIcon className="w-3 h-3" data-id="element-451" data-mp="117wy61b2hn27" /> {v.price}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => openModal(v.name)} className="bg-clinical-blue text-white font-black py-3 px-6 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] whitespace-nowrap shadow-md shadow-clinical-blue/10" data-id="element-452" data-mp="15zojnxfd92lh">
                      Schedule Appointment
                    </button>
                  </div>)}
              </div>
            </div>

            <div className="max-w-md" data-id="element-453" data-mp="3t27v5y5jd8hc">
              <div className="bg-clean-grey p-8 rounded-2xl border border-slate-100" data-id="element-454" data-mp="3t53wzxki8hb2">
                <MapPinIcon className="w-8 h-8 text-clinical-blue mb-4" data-id="element-455" data-mp="3o3wlpoj5kzzn" />
                <h3 className="text-lg font-bold text-slate-900 mb-2 uppercase tracking-tight" data-id="element-456" data-mp="3j5v7pi0l3qv1">
                  Annapolis Pharmacy
                </h3>
                <p className="text-slate-500 text-sm mb-4" data-id="element-457" data-mp="39fa1p9ikzk23">
                  2002 Jennifer Rd, Annapolis, MD 21401
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-widest" data-id="element-458" data-mp="35eq11vystyb7">
                  Walk-ins Welcome
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-459" data-mp="2ls6gi77gdpro">
            <div className="bg-clinical-blue p-8 md:p-10 rounded-3xl text-white" data-id="element-460" data-mp="2lr3kjkv9wx4w">
              <CalendarIcon className="w-12 h-12 mb-6 opacity-50" data-id="element-461" data-mp="2qs13hzo3xv7g" />
              <h2 className="text-2xl font-black mb-4 uppercase tracking-tight" data-id="element-462" data-mp="2btyydl1fh1o3">
                Quick Scheduling
              </h2>
              <p className="text-blue-100 mb-8 text-sm leading-relaxed" data-id="element-463" data-mp="2gtv35mhy8x4p">
                Most vaccinations take less than 15 minutes. Schedule now to
                skip the wait.
              </p>
              <button onClick={() => openModal('General Vaccination')} className="w-full bg-white text-clinical-blue font-black py-4 rounded-xl hover:bg-blue-50 transition-colors uppercase tracking-widest text-[10px] shadow-xl" data-id="element-464" data-mp="39gfvap691d9b">
                Book General Visit
              </button>
            </div>

            <div className="p-8 bg-slate-900 rounded-3xl text-white" data-id="element-465" data-mp="3nfflw2dbo284">
              <h3 className="text-lg font-bold mb-4 uppercase tracking-tight" data-id="element-466" data-mp="3s9a04qa5rozt">
                What to Bring
              </h3>
              <ul className="space-y-3" data-id="element-467" data-mp="1btwvg9jpceb0">
                {['Insurance Card', 'Photo ID', 'Vaccination Record'].map((item, i) => <li key={i} className="flex items-center gap-3 text-sm text-slate-400" data-id="element-468" data-mp="1qhhzc5oeihjz" data-mpi={i}>
                      <CheckCircleIcon className="w-4 h-4 text-clinical-blue" data-id="element-469" data-mp="xtvteqrpb0ty" />
                      {item}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <VaccineAppointmentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} vaccineName={selectedVaccine} data-id="element-470" data-mp="16vloq7opejx0" />
    </motion.div>;
}